import { ComponentProps } from 'react';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { IconButton } from '@creditornot/cb-button';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { TableRowAction } from './types';

const PopoverContent = styled.div<{ $isGrouped: boolean }>`
  padding: ${({ $isGrouped }) => ($isGrouped ? '16px' : '8px')};
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: auto;
`;

const Root = styled.div`
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type RowActionsProps<Row> = {
  items: TableRowAction<Row>[];
  record: Row;
};

const ActionButton = ({ children }: { children: ComponentProps<typeof Popover>['content'] }) => {
  return (
    <Popover placement="bottom-end" content={children} renderArrow={false} offset={8} zIndex={2}>
      {({ refProps }) => {
        return (
          <IconButton
            data-test-id="row-menu-button"
            size="small"
            variant="subdued"
            background="transparent"
            icon={icons.ellipsis}
            {...refProps}
          />
        );
      }}
    </Popover>
  );
};

export const RowMenu = <Row extends Record<string, unknown>>({
  items,
  record,
}: RowActionsProps<Row>) => {
  const f = useFormatMessage();
  if (!items?.length) return null;

  const groupedActions = items.reduce(
    (acc, item) => {
      const groupName = item.groupName || f('components.table.others');
      acc[groupName] = acc[groupName] ? [...acc[groupName], item] : [item];
      return acc;
    },
    {} as { [key: string]: TableRowAction<Row>[] },
  );

  const isGrouped = items.some((item) => item.groupName);

  return (
    <Root>
      <ActionButton>
        {({ close }) => (
          <PopoverContent $isGrouped={isGrouped}>
            {isGrouped &&
              Object.keys(groupedActions).map((groupName) => {
                return (
                  <MenuItemGroup title={groupName} key={groupName}>
                    {groupedActions[groupName].map(
                      ({ onClick, variant, icon, label, isDisabled }, index) => (
                        <MenuActionItem
                          disabled={isDisabled}
                          data-test-id={`row-menu-item-${index}`}
                          key={index}
                          icon={icon}
                          onClick={() => {
                            if (onClick) {
                              onClick(record);
                              close();
                            }
                          }}
                          variant={variant || 'black'}
                        >
                          {label}
                        </MenuActionItem>
                      ),
                    )}
                  </MenuItemGroup>
                );
              })}

            {!isGrouped &&
              items.map(({ onClick, variant, icon, label, isDisabled }, index) => (
                <MenuActionItem
                  disabled={isDisabled}
                  key={index}
                  icon={icon}
                  data-test-id={`row-menu-item-${index}`}
                  variant={variant || 'black'}
                  onClick={() => {
                    if (onClick) {
                      onClick(record);
                      close();
                    }
                  }}
                >
                  {label}
                </MenuActionItem>
              ))}
          </PopoverContent>
        )}
      </ActionButton>
    </Root>
  );
};
